import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from './client';

export type AdminRole = 'admin' | 'support' | 'super_admin';
export type AssignableAdminRole = 'admin' | 'support';
export type AdminStatus = 'pending' | 'active' | 'disabled';

export interface AdminUserDto {
  id: string;
  email: string;
  displayName: string;
  role: AdminRole;
  status: AdminStatus;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InviteResult extends AdminUserDto {
  setupUrl: string;
}

export interface InviteInput {
  email: string;
  displayName: string;
  role: AssignableAdminRole;
}

export interface UpdateInput {
  displayName?: string;
  role?: AssignableAdminRole;
}

export interface SetupInput {
  token: string;
  newPassword: string;
}

const adminUsersKey = ['admin-users'] as const;
const adminUserKey = (id: string) => ['admin-user', id] as const;

export function useAdminUsers() {
  return useQuery({
    queryKey: adminUsersKey,
    queryFn: () => apiGet<AdminUserDto[]>('/api/admin-users'),
  });
}

export function useAdminUser(id: string | undefined) {
  return useQuery({
    queryKey: adminUserKey(id ?? ''),
    queryFn: () => apiGet<AdminUserDto>(`/api/admin-users/${id}`),
    enabled: !!id,
  });
}

export function useInviteAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteInput) => apiPost<InviteResult>('/api/admin-users', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminUsersKey });
    },
  });
}

export function useUpdateAdmin(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateInput) => apiPatch<AdminUserDto>(`/api/admin-users/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminUsersKey });
      qc.invalidateQueries({ queryKey: adminUserKey(id) });
    },
  });
}

export function useDisableAdmin(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<AdminUserDto>(`/api/admin-users/${id}/disable`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminUsersKey });
      qc.invalidateQueries({ queryKey: adminUserKey(id) });
    },
  });
}

export function useEnableAdmin(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<AdminUserDto>(`/api/admin-users/${id}/enable`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminUsersKey });
      qc.invalidateQueries({ queryKey: adminUserKey(id) });
    },
  });
}

export function useRegenerateToken(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<InviteResult>(`/api/admin-users/${id}/regenerate-token`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminUserKey(id) });
    },
  });
}

export function useSetupAdmin() {
  return useMutation({
    mutationFn: (input: SetupInput) => apiPost<void>('/api/admin-users/setup', input),
  });
}

export interface PromoteSuperAdminInput {
  reason: string;
  confirmEmail: string;
}

export function usePromoteSuperAdmin(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PromoteSuperAdminInput) =>
      apiPost<AdminUserDto>(`/api/admin-users/${id}/promote-to-super-admin`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminUsersKey });
      qc.invalidateQueries({ queryKey: adminUserKey(id) });
    },
  });
}
